import { ExportFormat } from '~components/forms/exportForm';
import SkopeiConnectService from '~services/skopeiConnectService';

import ApiError from './apiError';

class ExportService extends SkopeiConnectService {
	private readonly formatMap: Record<ExportFormat, string> = {
		xlsx: 'Xlsx',
		csv: 'Csv',
		pdf: 'Pdf',
	};

	/**
	 * Start an async metrics export via SSE. Resolves with the jobId when the export is completed.
	 *
	 * @param deviceId The id of the device to filter on.
	 * @param start The start date to filter on.
	 * @param end The end date to filter on.
	 * @param format The ExportFormat for the file.
	 *
	 * @returns The jobId to download the file.
	 */
	async exportMetrics(params: {
		deviceId: string;
		start: string;
		end: string;
		format: ExportFormat;
		overwrittenClientId?: string;
	}): Promise<string> {
		const endpoint = this.createEndpoint('v2', ['metrics', params.deviceId, 'export'], true);
		return await this.client
			.get(endpoint, {
				adapter: 'fetch',
				params: {
					start: params.start,
					end: params.end,
					format: this.formatMap[params.format],
				},
				responseType: 'stream',
				headers: {
					Accept: 'text/event-stream',
					OverwrittenClientId: params.overwrittenClientId,
				},
			})
			.then(async (response) => {
				const reader = response.data.getReader();
				const decoder = new TextDecoder();
				let jobId: string | null = null;
				let buffer = '';
				while (true) {
					const { done, value } = await reader.read();
					if (done) break;
					buffer += decoder.decode(value, { stream: true });
					const lines = buffer.split('\n');
					// Keep incomplete last line in buffer
					buffer = lines.pop() ?? '';
					for (const line of lines) {
						if (line.startsWith('data:')) {
							const data = JSON.parse(line.substring(5).trim());
							jobId = data.jobId;
						}
					}
					if (
						buffer.includes('event: completed') ||
						lines.some((l) => l.includes('event: completed'))
					) {
						break;
					}
				}
				if (!jobId) {
					return Promise.reject(ApiError.createFromCustomCode('exportCompletedWithoutJobId'));
				}

				return Promise.resolve(jobId);
			});
	}

	/**
	 * Export keys with the current grid filters applied.
	 *
	 * @param search The current search filter.
	 * @param clientId The client id to filter on.
	 * @param resourceGroupId The resource group id to filter on.
	 * @param type The key type to filter on.
	 *
	 * @returns The file blob.
	 */
	async exportKeys(params: {
		format: ExportFormat;
		search?: string;
		clientId?: string;
		resourceGroupId?: string;
		type?: string;
	}): Promise<Blob> {
		const endpoint = this.createEndpoint('v2', ['keys', 'export'], true);
		return await this.client
			.get(endpoint, {
				responseType: 'blob',
				params: {
					format: this.formatMap[params.format],
					search: params.search?.trim() || undefined,
					'client-id': params.clientId,
					'resource-group-id': params.resourceGroupId,
					type: params.type,
				},
			})
			.then((response) => Promise.resolve(response.data));
	}

	/**
	 * Export devices with the current grid filters applied.
	 *
	 * @param search The current search filter.
	 * @param model The client id to filter on.
	 * @param clientId The client id to filter on.
	 * @param resourceGroupId The resource group id to filter on.
	 *
	 * @returns The file blob.
	 */
	async exportDevices(params: {
		format: ExportFormat;
		search?: string;
		model?: string;
		clientId?: string;
		resourceGroupId?: string;
	}): Promise<Blob> {
		const endpoint = this.createEndpoint('v2', ['devices', 'export'], true);
		return await this.client
			.get(endpoint, {
				responseType: 'blob',
				params: {
					format: this.formatMap[params.format],
					search: params.search?.trim() || undefined,
					model: params.model,
					'client-id': params.clientId,
					'resource-group-id': params.resourceGroupId,
				},
			})
			.then((response) => Promise.resolve(response.data));
	}

	/**
	 * Download an exported file as a Blob.
	 */
	async downloadExport(params: { jobId: string }): Promise<Blob> {
		const endpoint = this.createEndpoint('v2', ['metrics', 'export', params.jobId], true);
		return await this.client
			.get<Blob>(endpoint, {
				responseType: 'blob',
			})
			.then((response) => Promise.resolve(response.data));
	}
}

export default ExportService;
